// orchestrator_fullstack/frontend/src/app/pages/LoginPage.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";

/* ----------------------------- helpers ----------------------------- */

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function getCachedRolesAndTenant(): { roles: string[]; tenant?: string } {
  try {
    const raw = localStorage.getItem("orchestrator.authme");
    if (!raw) return { roles: [] };
    const parsed = JSON.parse(raw);
    const me = parsed?.me;
    const roles = Array.isArray(me?.roles) ? me.roles : [];
    const tenant = typeof me?.tenant === "string" ? me.tenant : undefined;
    return { roles, tenant };
  } catch {
    return { roles: [] };
  }
}

function Divider({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "18px 0" }}>
      <div style={{ height: 1, background: "rgba(255,255,255,0.12)", flex: 1 }} />
      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.70)", fontWeight: 700 }}>{label}</div>
      <div style={{ height: 1, background: "rgba(255,255,255,0.12)", flex: 1 }} />
    </div>
  );
}

function HelpPill({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 10px",
        borderRadius: 999,
        border: "1px solid rgba(255,255,255,0.16)",
        background: "rgba(255,255,255,0.06)",
        fontSize: 12,
        color: "rgba(255,255,255,0.78)",
        fontWeight: 600,
        backdropFilter: "blur(10px)",
      }}
    >
      {children}
    </div>
  );
}

/* -------------------- CanvasLoginBackground -------------------- */

type CanvasBgProps = {
  className?: string;
  seed?: number;
  interactive?: boolean;
  mode?: "hero" | "full";
  intensity?: number;
  fixed?: boolean;
};

/** Small, fast seeded RNG (deterministic) */
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function clamp(v: number, a: number, b: number) {
  return Math.max(a, Math.min(b, v));
}
function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

type Node = {
  id: number;
  x: number;
  y: number;
  r: number;
  hue: number;
  lane: 0 | 1 | 2;
  pulse: number;
};

type Edge = {
  a: number;
  b: number;
  hue: number;
  w: number;
};

type Packet = {
  edgeIndex: number;
  t: number;
  speed: number;
  hue: number;
  size: number;
  phase: number;
};

function CanvasLoginBackground({
  className,
  seed,
  interactive = false,
  mode = "full",
  intensity = 1.02,
  fixed = true,
}: CanvasBgProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    let mounted = true;
    let w = 0;
    let h = 0;
    let dpr = 1;

    const seeded = typeof seed === "number";
    const rng = seeded ? mulberry32(seed!) : null;
    const rand01 = () => (seeded ? rng!() : Math.random());
    const rand = (a: number, b: number) => a + rand01() * (b - a);

    const pointer = { x: 0, y: 0, active: false };
    const onMove = (e: PointerEvent) => {
      if (!interactive) return;
      const rect = canvas.getBoundingClientRect();
      pointer.x = e.clientX - rect.left;
      pointer.y = e.clientY - rect.top;
      pointer.active = true;
    };
    const onLeave = () => {
      if (!interactive) return;
      pointer.active = false;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      const rect = (parent ?? canvas).getBoundingClientRect();

      w = Math.max(1, Math.floor(rect.width));
      h = Math.max(1, Math.floor(rect.height));
      dpr = clamp(window.devicePixelRatio || 1, 1, 2);

      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    const ro = new ResizeObserver(() => resize());
    ro.observe(canvas.parentElement ?? canvas);

    if (interactive) {
      window.addEventListener("pointermove", onMove, { passive: true });
      window.addEventListener("pointerleave", onLeave as any, { passive: true } as any);
    }

    const modeScale = mode === "full" ? 1.0 : 0.92;

    const bgGradient = () => {
      const g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, "rgba(4, 8, 20, 1)");
      g.addColorStop(0.35, "rgba(8, 18, 48, 1)");
      g.addColorStop(1, "rgba(2, 3, 8, 1)");
      return g;
    };

    const vignette = () => {
      const rg = ctx.createRadialGradient(
        w * 0.52,
        h * 0.46,
        0,
        w * 0.52,
        h * 0.46,
        Math.max(w, h) * 0.9
      );
      rg.addColorStop(0, "rgba(0,0,0,0)");
      rg.addColorStop(0.62, "rgba(0,0,0,0.16)");
      rg.addColorStop(1, "rgba(0,0,0,0.72)");
      return rg;
    };

    const hsla = (hue: number, a: number, l = 62) => `hsla(${hue}, 92%, ${l}%, ${a})`;

    const drawGrid = () => {
      ctx.save();
      ctx.globalAlpha = 0.12;
      ctx.strokeStyle = "rgba(255,255,255,0.08)";
      ctx.lineWidth = 1;
      const step = (mode === "full" ? 56 : 52) * modeScale;
      for (let x = 0; x <= w; x += step) {
        ctx.beginPath();
        ctx.moveTo(x + 0.5, 0);
        ctx.lineTo(x + 0.5, h);
        ctx.stroke();
      }
      for (let y = 0; y <= h; y += step) {
        ctx.beginPath();
        ctx.moveTo(0, y + 0.5);
        ctx.lineTo(w, y + 0.5);
        ctx.stroke();
      }
      ctx.restore();
    };

    const drawLaneRails = (t: number) => {
      const lanes = [
        { y: 0.24, hue: 200 },
        { y: 0.5, hue: 235 },
        { y: 0.76, hue: 270 },
      ] as const;

      ctx.save();
      ctx.globalCompositeOperation = "lighter";

      for (let i = 0; i < lanes.length; i++) {
        const ly = h * lanes[i].y;
        const hue = lanes[i].hue;

        const g = ctx.createLinearGradient(0, ly, w, ly);
        const a = 0.18 * intensity;
        g.addColorStop(0, hsla(hue, 0.0, 62));
        g.addColorStop(0.22, hsla(hue, a, 64));
        g.addColorStop(0.5, hsla(hue + 18, a * 0.95, 64));
        g.addColorStop(0.78, hsla(hue + 34, a * 0.9, 64));
        g.addColorStop(1, hsla(hue, 0.0, 62));

        ctx.globalAlpha = 0.22 * intensity;
        ctx.strokeStyle = g;
        ctx.lineWidth = 10 * modeScale;
        ctx.beginPath();
        ctx.moveTo(w * 0.06, ly + Math.sin(t * 0.55 + i) * 1.2);
        ctx.lineTo(w * 0.94, ly + Math.sin(t * 0.55 + i) * 1.2);
        ctx.stroke();

        ctx.globalAlpha = 0.55 * intensity;
        ctx.lineWidth = 1.2;
        ctx.strokeStyle = "rgba(255,255,255,0.12)";
        ctx.beginPath();
        ctx.moveTo(w * 0.06, ly + 0.5);
        ctx.lineTo(w * 0.94, ly + 0.5);
        ctx.stroke();

        ctx.globalAlpha = 0.22 * intensity;
        ctx.strokeStyle = hsla(hue + 10, 0.25 * intensity, 66);
        ctx.lineWidth = 1;
        const tickStep = 64 * modeScale;
        for (let x = w * 0.08; x <= w * 0.92; x += tickStep) {
          const th = 8 + 3 * Math.sin(t * 1.2 + x * 0.01 + i);
          ctx.beginPath();
          ctx.moveTo(x, ly - th * 0.5);
          ctx.lineTo(x, ly + th * 0.5);
          ctx.stroke();
        }
      }

      ctx.restore();
      ctx.globalCompositeOperation = "source-over";
    };

    const drawCenterReadabilityMask = () => {
      ctx.save();
      const cx = w * 0.5;
      const cy = h * 0.52;
      const rx = Math.min(w, 720) * 0.62;
      const ry = Math.min(h, 560) * 0.46;

      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(rx, ry));
      g.addColorStop(0, "rgba(0,0,0,0.28)");
      g.addColorStop(0.55, "rgba(0,0,0,0.16)");
      g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    };

    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const packets: Packet[] = [];

    function roundRect(c: CanvasRenderingContext2D, x: number, y: number, ww: number, hh: number, rr: number) {
      const r = Math.min(rr, ww / 2, hh / 2);
      c.beginPath();
      c.moveTo(x + r, y);
      c.lineTo(x + ww - r, y);
      c.quadraticCurveTo(x + ww, y, x + ww, y + r);
      c.lineTo(x + ww, y + hh - r);
      c.quadraticCurveTo(x + ww, y + hh, x + ww - r, y + hh);
      c.lineTo(x + r, y + hh);
      c.quadraticCurveTo(x, y + hh, x, y + hh - r);
      c.lineTo(x, y + r);
      c.quadraticCurveTo(x, y, x + r, y);
      c.closePath();
    }

    const rebuildGraph = () => {
      nodes.length = 0;
      edges.length = 0;
      packets.length = 0;

      const left = w * 0.08;
      const right = w * 0.92;
      const laneY = [h * 0.24, h * 0.5, h * 0.76] as const;

      const laneCounts: Array<number> = [
        clamp(Math.round((w / 520) * 5), 4, 6),
        clamp(Math.round((w / 520) * 6), 5, 8),
        clamp(Math.round((w / 520) * 4), 3, 6),
      ];

      let id = 0;
      for (let lane = 0 as 0 | 1 | 2; lane <= 2; lane = (lane + 1) as any) {
        const n = laneCounts[lane];
        for (let i = 0; i < n; i++) {
          const t = n === 1 ? 0.5 : i / (n - 1);
          const x = lerp(left, right, t);
          const y =
            laneY[lane] +
            rand(-20, 20) * modeScale +
            (lane === 1 ? rand(-14, 14) * modeScale : 0);

          const cx = w * 0.5;
          const dx = x - cx;
          const outward = Math.sign(dx) * 10 * modeScale;
          const xx = clamp(x + outward, left, right);

          const hue = lane === 0 ? rand(195, 220) : lane === 1 ? rand(220, 255) : rand(255, 295);

          nodes.push({
            id: id++,
            x: xx,
            y,
            r: rand(3.0, 4.8) * (0.9 + intensity * 0.18) * modeScale,
            hue,
            lane,
            pulse: rand(0, Math.PI * 2),
          });
        }
      }

      const byLane = (lane: 0 | 1 | 2) => nodes.filter((n) => n.lane === lane);

      const addEdge = (a: Node, b: Node, hue: number, wv: number) => {
        edges.push({ a: a.id, b: b.id, hue, w: wv });
      };

      ([0, 1, 2] as const).forEach((lane) => {
        const arr = byLane(lane).sort((aa, bb) => aa.x - bb.x);
        for (let i = 0; i < arr.length - 1; i++) {
          const a = arr[i];
          const b = arr[i + 1];
          addEdge(a, b, lerp(a.hue, b.hue, 0.5), lane === 1 ? 1.35 : 1.2);
          if (rand01() < 0.35 && i + 2 < arr.length) {
            const c = arr[i + 2];
            addEdge(a, c, lerp(a.hue, c.hue, 0.5), 1.0);
          }
        }
      });

      const control = byLane(0).sort((aa, bb) => aa.x - bb.x);
      const runtime = byLane(1).sort((aa, bb) => aa.x - bb.x);
      const obs = byLane(2).sort((aa, bb) => aa.x - bb.x);

      const connectHandoffs = (upper: Node[], lower: Node[], count: number, hueBase: number) => {
        for (let i = 0; i < count; i++) {
          const ua = upper[clamp(Math.floor(rand01() * upper.length), 0, upper.length - 1)];
          let candidates = lower.filter((n) => n.x > ua.x - w * 0.06);
          if (candidates.length === 0) candidates = lower;
          const lb = candidates[Math.floor(rand01() * candidates.length)];
          addEdge(ua, lb, hueBase + rand(-10, 10), 1.05);
        }
      };

      connectHandoffs(control, runtime, clamp(Math.round(runtime.length * 0.7), 3, 6), 218);
      connectHandoffs(runtime, obs, clamp(Math.round(obs.length * 0.7), 2, 5), 265);

      const edgeCount = edges.length;
      const packetCount = clamp(Math.round(edgeCount * 0.65), 12, 32);

      for (let i = 0; i < packetCount; i++) {
        const ei = Math.floor(rand01() * edgeCount);
        const e = edges[ei];
        packets.push({
          edgeIndex: ei,
          t: rand01(),
          speed: rand(0.08, 0.22) * (0.9 + intensity * 0.15) * (prefersReducedMotion ? 0 : 1),
          hue: e.hue + rand(-8, 8),
          size: rand(1.6, 2.6) * modeScale,
          phase: rand(0, Math.PI * 2),
        });
      }
    };

    rebuildGraph();

    const drawCardNodes = (t: number) => {
      ctx.save();
      ctx.globalCompositeOperation = "lighter";

      const cx = w * 0.5;
      const cy = h * 0.52;
      const centerDenyR = Math.min(w, h) * 0.2;

      for (const n of nodes) {
        const dx = n.x - cx;
        const dy = n.y - cy;
        const cd = Math.sqrt(dx * dx + dy * dy);
        const fade = clamp((cd - centerDenyR) / (centerDenyR * 0.9), 0, 1);

        if (fade < 0.15) continue;
        if (n.id % 3 !== 0) continue;

        const ww = (48 + (n.lane === 1 ? 24 : 12)) * modeScale;
        const hh = (22 + (n.lane === 1 ? 10 : 6)) * modeScale;
        const x = n.x - ww * 0.5;
        const y = n.y - hh * 0.5;

        ctx.globalAlpha = 0.12 * intensity * fade;
        ctx.fillStyle = `rgba(255,255,255,0.06)`;
        roundRect(ctx, x, y, ww, hh, 8 * modeScale);
        ctx.fill();

        ctx.globalAlpha = 0.18 * intensity * fade;
        ctx.strokeStyle = hsla(n.hue, 0.22 * intensity * fade, 66);
        ctx.lineWidth = 1;
        roundRect(ctx, x, y, ww, hh, 8 * modeScale);
        ctx.stroke();

        ctx.globalAlpha = 0.16 * intensity * fade;
        ctx.strokeStyle = "rgba(255,255,255,0.12)";
        ctx.beginPath();
        ctx.moveTo(x + 10 * modeScale, y + 8 * modeScale);
        ctx.lineTo(x + ww - 10 * modeScale, y + 8 * modeScale);
        ctx.stroke();
      }

      ctx.restore();
      ctx.globalCompositeOperation = "source-over";
    };

    const drawEdges = (t: number) => {
      ctx.save();
      ctx.globalCompositeOperation = "lighter";

      let px = 0;
      let py = 0;
      if (interactive && pointer.active) {
        const cx = w * 0.5;
        const cy = h * 0.5;
        px = (pointer.x - cx) * 0.015;
        py = (pointer.y - cy) * 0.015;
      }

      const cx = w * 0.5;
      const cy = h * 0.52;
      const centerDenyR = Math.min(w, h) * 0.2;

      for (let i = 0; i < edges.length; i++) {
        const e = edges[i];
        const a = nodes[e.a];
        const b = nodes[e.b];
        if (!a || !b) continue;

        const mx = (a.x + b.x) * 0.5;
        const my = (a.y + b.y) * 0.5;
        const ddx = mx - cx;
        const ddy = my - cy;
        const cd = Math.sqrt(ddx * ddx + ddy * ddy);
        const fade = clamp((cd - centerDenyR) / (centerDenyR * 0.85), 0, 1);

        const ax = a.x + px;
        const ay = a.y + py;
        const bx = b.x + px;
        const by = b.y + py;

        const midX = lerp(ax, bx, 0.55);

        const glowA = 0.08 * intensity * fade;
        const coreA = 0.18 * intensity * fade;

        ctx.globalAlpha = glowA;
        ctx.strokeStyle = hsla(e.hue, 0.35 * intensity * fade, 64);
        ctx.lineWidth = e.w * 3.0 * modeScale;
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(midX, ay);
        ctx.lineTo(midX, by);
        ctx.lineTo(bx, by);
        ctx.stroke();

        ctx.globalAlpha = coreA;
        ctx.strokeStyle = `rgba(255,255,255,${0.1 * fade})`;
        ctx.lineWidth = e.w * 1.0 * modeScale;
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(midX, ay);
        ctx.lineTo(midX, by);
        ctx.lineTo(bx, by);
        ctx.stroke();
      }

      ctx.restore();
      ctx.globalCompositeOperation = "source-over";
    };

    const drawNodes = (t: number) => {
      ctx.save();
      ctx.globalCompositeOperation = "lighter";

      let px = 0;
      let py = 0;
      if (interactive && pointer.active) {
        const cx = w * 0.5;
        const cy = h * 0.5;
        px = (pointer.x - cx) * 0.02;
        py = (pointer.y - cy) * 0.02;
      }

      const cx = w * 0.5;
      const cy = h * 0.52;
      const centerDenyR = Math.min(w, h) * 0.2;

      for (const n of nodes) {
        n.pulse += 0.015;

        const dx = n.x - cx;
        const dy = n.y - cy;
        const cd = Math.sqrt(dx * dx + dy * dy);
        const fade = clamp((cd - centerDenyR) / (centerDenyR * 0.8), 0, 1);

        const tw = 0.6 + 0.4 * Math.sin(t * 1.2 + n.pulse);
        const rr = n.r * (0.95 + 0.18 * tw);

        const x = n.x + px;
        const y = n.y + py;

        const glowR = rr * 7.0;
        const g = ctx.createRadialGradient(x, y, 0, x, y, glowR);
        g.addColorStop(0, `hsla(${n.hue}, 95%, 65%, ${0.28 * intensity * tw * fade})`);
        g.addColorStop(0.35, `hsla(${n.hue}, 95%, 62%, ${0.1 * intensity * tw * fade})`);
        g.addColorStop(1, `hsla(${n.hue}, 95%, 60%, 0)`);

        ctx.globalAlpha = 1;
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, glowR, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `hsla(${n.hue}, 98%, 66%, ${0.7 * tw * fade + 0.1})`;
        ctx.beginPath();
        ctx.arc(x, y, rr, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
      ctx.globalCompositeOperation = "source-over";
    };

    const drawPackets = (t: number) => {
      if (prefersReducedMotion) return;

      ctx.save();
      ctx.globalCompositeOperation = "lighter";

      let px = 0;
      let py = 0;
      if (interactive && pointer.active) {
        const cx = w * 0.5;
        const cy = h * 0.5;
        px = (pointer.x - cx) * 0.012;
        py = (pointer.y - cy) * 0.012;
      }

      const cx = w * 0.5;
      const cy = h * 0.52;
      const centerDenyR = Math.min(w, h) * 0.18;

      for (const p of packets) {
        const e = edges[p.edgeIndex];
        const a = nodes[e.a];
        const b = nodes[e.b];
        if (!a || !b) continue;

        p.t += p.speed * 0.01 * (0.85 + 0.15 * Math.sin(t * 1.1 + p.phase));
        if (p.t > 1) p.t = 0;

        const ax = a.x + px;
        const ay = a.y + py;
        const bx = b.x + px;
        const by = b.y + py;

        const midX = lerp(ax, bx, 0.55);

        const seg1 = Math.abs(midX - ax);
        const seg2 = Math.abs(by - ay);
        const seg3 = Math.abs(bx - midX);
        const total = seg1 + seg2 + seg3 + 1e-6;

        const dist = p.t * total;
        let x = ax;
        let y = ay;

        if (dist <= seg1) {
          const tt = dist / seg1;
          x = lerp(ax, midX, tt);
          y = ay;
        } else if (dist <= seg1 + seg2) {
          const tt = (dist - seg1) / seg2;
          x = midX;
          y = lerp(ay, by, tt);
        } else {
          const tt = (dist - seg1 - seg2) / seg3;
          x = lerp(midX, bx, tt);
          y = by;
        }

        const dx = x - cx;
        const dy = y - cy;
        const cd = Math.sqrt(dx * dx + dy * dy);
        const fade = clamp((cd - centerDenyR) / (centerDenyR * 0.9), 0, 1);

        const tw = 0.65 + 0.35 * Math.sin(t * 2.2 + p.phase);
        const size = p.size * (0.9 + 0.3 * tw);

        const glowR = size * 8;
        const g = ctx.createRadialGradient(x, y, 0, x, y, glowR);
        g.addColorStop(0, `hsla(${p.hue}, 95%, 65%, ${0.36 * intensity * tw * fade})`);
        g.addColorStop(0.35, `hsla(${p.hue}, 95%, 62%, ${0.12 * intensity * tw * fade})`);
        g.addColorStop(1, `hsla(${p.hue}, 95%, 60%, 0)`);

        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, glowR, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `hsla(${p.hue}, 98%, 66%, ${0.85 * tw * fade})`;
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
      ctx.globalCompositeOperation = "source-over";
    };

    const drawNoise = () => {
      ctx.save();
      ctx.globalAlpha = 0.045;
      ctx.fillStyle = "rgba(255,255,255,0.10)";
      const step = 3;

      const noiseRng = seeded ? mulberry32((seed! ^ 0x71bdc2a7) >>> 0) : null;
      const noise01 = () => (seeded ? noiseRng!() : Math.random());

      for (let i = 0; i < 720; i++) {
        const x = (noise01() * w) | 0;
        const y = (noise01() * h) | 0;
        ctx.fillRect(x, y, step, step);
      }
      ctx.restore();
    };

    let last = performance.now();
    let time = 0;

    const tick = (now: number) => {
      if (!mounted) return;

      const dt = Math.min(40, now - last);
      last = now;

      const speed = prefersReducedMotion ? 0 : 0.001 * intensity;
      time += dt * speed;

      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = bgGradient();
      ctx.fillRect(0, 0, w, h);

      drawGrid();

      ctx.save();
      ctx.globalAlpha = 0.45;
      ctx.globalCompositeOperation = "lighter";

      const blob1 = ctx.createRadialGradient(w * 0.22, h * 0.26, 0, w * 0.22, h * 0.26, Math.max(w, h) * 0.5);
      blob1.addColorStop(0, `rgba(34, 211, 238, ${0.16 * intensity})`);
      blob1.addColorStop(1, "rgba(34, 211, 238, 0)");
      ctx.fillStyle = blob1;
      ctx.fillRect(0, 0, w, h);

      const blob2 = ctx.createRadialGradient(w * 0.82, h * 0.55, 0, w * 0.82, h * 0.55, Math.max(w, h) * 0.58);
      blob2.addColorStop(0, `rgba(99, 102, 241, ${0.13 * intensity})`);
      blob2.addColorStop(1, "rgba(99, 102, 241, 0)");
      ctx.fillStyle = blob2;
      ctx.fillRect(0, 0, w, h);

      ctx.restore();
      ctx.globalCompositeOperation = "source-over";

      drawLaneRails(time);
      drawCenterReadabilityMask();

      drawCardNodes(time);
      drawEdges(time);
      drawPackets(time);
      drawNodes(time);

      ctx.fillStyle = vignette();
      ctx.fillRect(0, 0, w, h);

      drawNoise();

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    const resizeAndRebuild = () => {
      resize();
      rebuildGraph();
    };
    const ro2 = new ResizeObserver(() => resizeAndRebuild());
    ro2.observe(canvas.parentElement ?? canvas);

    return () => {
      mounted = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      ro2.disconnect();
      if (interactive) {
        window.removeEventListener("pointermove", onMove as any);
        window.removeEventListener("pointerleave", onLeave as any);
      }
    };
  }, [seed, interactive, mode, intensity, prefersReducedMotion]);

  return (
    <div
      className={className}
      style={{
        position: fixed ? "fixed" : "absolute",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
      }}
      aria-hidden="true"
    >
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: "100%",
          display: "block",
        }}
      />
    </div>
  );
}

/* ----------------------------- Google GIS types ----------------------------- */

declare global {
  interface Window {
    google?: any;
    __ENV__?: Record<string, any>;
  }
}

/* ----------------------------- LoginPage ----------------------------- */

export default function LoginPage() {
  const [token, setToken] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [googleErr, setGoogleErr] = useState<string | null>(null);
  const [googleBusy, setGoogleBusy] = useState(false);

  const { signInWithToken } = useAuth();
  const nav = useNavigate();
  const q = useQuery();

  const defaultNext = "/admin/overview";
  const nextParam = q.get("next");
  const next = nextParam || defaultNext;

  const googleClientId = useMemo(() => {
    try {
      const rt =
        String(window.__ENV__?.GOOGLE_CLIENT_ID || window.__ENV__?.VITE_GOOGLE_CLIENT_ID || "").trim();
      if (rt) return rt;
    } catch {}
    return String((import.meta as any)?.env?.VITE_GOOGLE_CLIENT_ID || "").trim();
  }, []);

  const oauthDisabledMsg =
    "Google Client ID is missing. Set window.__ENV__.GOOGLE_CLIENT_ID at runtime or VITE_GOOGLE_CLIENT_ID at build time.";

  const googleHostRef = useRef<HTMLDivElement | null>(null);
  const initDoneRef = useRef(false);

  const isLikelyJwt = useCallback((s: string) => {
    const t = (s || "").trim();
    return t.split(".").length === 3 && t.length > 40;
  }, []);

  const roleBasedRedirect = useCallback(() => {
    if (nextParam) {
      nav(nextParam, { replace: true });
      return;
    }

    const { roles } = getCachedRolesAndTenant();
    const has = (r: string) => roles.includes(r);

    if (has("platform-admin") || has("admin")) {
      nav("/admin/overview", { replace: true });
    } else if (has("operator")) {
      nav("/admin/observability/platform", { replace: true });
    } else {
      nav("/login?next=%2Fadmin%2Fobservability%2Fplatform", { replace: true });
    }
  }, [nav, nextParam]);

  const waitForGoogleApi = useCallback(async () => {
    for (let i = 0; i < 80; i++) {
      if (window.google?.accounts?.id) return;
      await new Promise((r) => setTimeout(r, 50));
    }
    throw new Error("Google GIS did not become available after script load.");
  }, []);

  const loadGsiScript = useCallback(async () => {
    const src = "https://accounts.google.com/gsi/client";

    if (window.google?.accounts?.id) return;

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      await waitForGoogleApi();
      return;
    }

    await new Promise<void>((resolve, reject) => {
      const s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      s.onerror = () =>
        reject(
          new Error(
            "Google GIS script failed to load. Check CSP/adblockers and allow https://accounts.google.com in script-src."
          )
        );
      document.head.appendChild(s);
    });

    await waitForGoogleApi();
  }, [waitForGoogleApi]);

  useEffect(() => {
    let cancelled = false;

    async function initGoogle() {
      setGoogleErr(null);

      if (!googleClientId) {
        initDoneRef.current = false;
        return;
      }

      try {
        await loadGsiScript();
        if (cancelled) return;

        const g = window.google;
        if (!g?.accounts?.id) {
          throw new Error("Google GIS is unavailable.");
        }

        const host = googleHostRef.current;
        if (!host) return;

        if (!initDoneRef.current) {
          g.accounts.id.initialize({
            client_id: googleClientId,
            ux_mode: "popup",
            callback: async (resp: { credential?: string }) => {
              try {
                setGoogleErr(null);
                const cred = String(resp?.credential || "").trim();

                if (!cred || !isLikelyJwt(cred)) {
                  setGoogleErr("Google returned an empty or invalid credential.");
                  return;
                }

                setGoogleBusy(true);
                await signInWithToken(cred);
                roleBasedRedirect();
              } catch (e: any) {
                setGoogleErr(e?.message || "Google sign-in failed.");
              } finally {
                setGoogleBusy(false);
              }
            },
          });

          initDoneRef.current = true;
        }

        host.innerHTML = "";
        g.accounts.id.renderButton(host, {
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "rectangular",
          width: 360,
        });
      } catch (e: any) {
        if (!cancelled) {
          setGoogleErr(e?.message || "Failed to initialize Google sign-in.");
        }
      }
    }

    initGoogle();

    return () => {
      cancelled = true;
    };
  }, [googleClientId, isLikelyJwt, loadGsiScript, roleBasedRedirect, signInWithToken]);

  return (
    <div style={{ minHeight: "100vh", position: "relative", overflow: "hidden" }}>
      <CanvasLoginBackground mode="full" fixed interactive={false} intensity={1.02} />

      <div
        style={{
          position: "relative",
          zIndex: 10,
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: 24,
        }}
      >
        <div
          style={{
            width: "min(520px, 100%)",
            borderRadius: 18,
            border: "1px solid rgba(255,255,255,0.14)",
            background: "rgba(8, 12, 24, 0.72)",
            backdropFilter: "blur(14px)",
            padding: 22,
            boxShadow: "0 14px 40px rgba(0,0,0,0.45)",
            color: "rgba(255,255,255,0.92)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              aria-hidden
              style={{
                height: 34,
                width: 34,
                borderRadius: 12,
                background: "linear-gradient(135deg, rgba(34,211,238,0.92), rgba(99,102,241,0.92))",
                boxShadow: "0 10px 24px rgba(0,0,0,0.35)",
              }}
            />
            <div>
              <div
                style={{
                  fontSize: 12,
                  letterSpacing: 1.2,
                  fontWeight: 800,
                  color: "rgba(255,255,255,0.70)",
                }}
              >
                ONE INTELLIGENCE (1-i)
              </div>
              <div style={{ fontSize: 18, fontWeight: 900, lineHeight: 1.1 }}>AI Control Plane</div>
            </div>
          </div>

          <div style={{ marginTop: 14, fontSize: 22, fontWeight: 950, lineHeight: 1.2 }}>Sign in to continue</div>

          <div style={{ marginTop: 8, fontSize: 13, color: "rgba(255,255,255,0.70)", lineHeight: 1.5 }}>
            Continue with Google, or use the advanced JWT token option if needed.
          </div>

          <div style={{ marginTop: 10, fontSize: 12, color: "rgba(255,255,255,0.60)" }} title={next}>
            Next: {next}
          </div>

          <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 10 }}>
            <HelpPill>Ingress apps</HelpPill>
            <HelpPill>Deterministic workflows</HelpPill>
            <HelpPill>Observability</HelpPill>
          </div>

          <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
            <div
              style={{
                width: "100%",
                borderRadius: 14,
                border: "1px solid rgba(255,255,255,0.16)",
                background: googleClientId ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.06)",
                padding: "10px 12px",
                display: "flex",
                justifyContent: "center",
                opacity: googleClientId ? 1 : 0.55,
                pointerEvents: googleClientId && !googleBusy ? "auto" : "none",
              }}
              title={googleClientId ? (googleBusy ? "Signing in..." : "Continue with Google") : oauthDisabledMsg}
              aria-disabled={!googleClientId || googleBusy}
            >
              <div ref={googleHostRef} />
            </div>

            {googleErr && (
              <div
                style={{
                  padding: 12,
                  borderRadius: 14,
                  background: "rgba(255,0,0,0.10)",
                  border: "1px solid rgba(255,0,0,0.22)",
                  color: "rgba(255,220,220,0.95)",
                  fontWeight: 700,
                  fontSize: 12,
                  lineHeight: 1.4,
                  whiteSpace: "pre-wrap",
                }}
              >
                {googleErr}
              </div>
            )}
          </div>

          <Divider label="OR" />

          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            style={{
              width: "100%",
              textAlign: "left",
              padding: "10px 12px",
              borderRadius: 14,
              border: "1px solid rgba(255,255,255,0.14)",
              background: "rgba(255,255,255,0.06)",
              fontWeight: 900,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              color: "rgba(255,255,255,0.92)",
              cursor: "pointer",
            }}
          >
            <span>Advanced: Paste JWT</span>
            <span style={{ fontSize: 12, opacity: 0.75 }}>{showAdvanced ? "Hide" : "Show"}</span>
          </button>

          {showAdvanced && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (busy) return;

                const t = token.trim();
                if (!t) {
                  setErr("Paste a JWT token.");
                  return;
                }

                setBusy(true);
                setErr(null);

                try {
                  await signInWithToken(t);
                  roleBasedRedirect();
                } catch (e2: any) {
                  setErr(e2?.message || "Sign-in failed");
                } finally {
                  setBusy(false);
                }
              }}
              style={{ marginTop: 10 }}
            >
              <textarea
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="eyJhbGciOiJSUzI1NiIs..."
                rows={8}
                style={{
                  width: "100%",
                  borderRadius: 14,
                  border: "1px solid rgba(255,255,255,0.14)",
                  padding: 12,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  fontSize: 12,
                  outline: "none",
                  background: "rgba(255,255,255,0.06)",
                  color: "rgba(255,255,255,0.92)",
                }}
              />

              {err && (
                <div
                  style={{
                    marginTop: 10,
                    padding: 12,
                    background: "rgba(255,0,0,0.10)",
                    borderRadius: 14,
                    color: "rgba(255,220,220,0.95)",
                    fontWeight: 700,
                    fontSize: 12,
                    lineHeight: 1.4,
                    border: "1px solid rgba(255,0,0,0.22)",
                  }}
                >
                  {err}
                </div>
              )}

              <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                <button
                  type="submit"
                  disabled={busy || !token.trim()}
                  style={{
                    flex: 1,
                    padding: "10px 14px",
                    fontWeight: 950,
                    borderRadius: 14,
                    border: "1px solid rgba(255,255,255,0.16)",
                    background: busy ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.92)",
                    color: busy ? "rgba(255,255,255,0.72)" : "rgba(0,0,0,0.88)",
                    cursor: busy ? "not-allowed" : "pointer",
                  }}
                >
                  {busy ? "Signing in..." : "Continue"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setToken("");
                    setErr(null);
                  }}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 14,
                    border: "1px solid rgba(255,255,255,0.14)",
                    background: "rgba(255,255,255,0.06)",
                    color: "rgba(255,255,255,0.88)",
                    fontWeight: 900,
                    cursor: "pointer",
                  }}
                >
                  Clear
                </button>
              </div>
            </form>
          )}

          <div style={{ marginTop: 14, fontSize: 12, color: "rgba(255,255,255,0.62)" }}>
              Need access to the entire platform? Request Demo.
          </div>
        </div>
      </div>
    </div>
  );
}